import * as THREE from 'three';
import toast from 'react-hot-toast';
import store from '../redux/store';
import { update_assets_data } from '../redux/Slice/gridSlice';
import { updateParticle } from '../redux/Slice/particleSlice';
import { resolveSceneObject } from './ThreeJsUtils/assetVisibilityUtils';

/** Must match pivotEditor.PIVOT_RESTORE_EVENT_ACTION (avoid circular import). */
const PIVOT_RESTORE_EVENT_ACTION = '__pivot_restore__';

function isInternalPivotRestoreEvent(event) {
  return event?.action === PIVOT_RESTORE_EVENT_ACTION;
}

const ASSET_LIST_KEYS = ['mesh', 'image', 'video', 'audio', 'text', 'hotspot'];

const ASSET_TYPE_BY_DELETE_TYPE = {
  mesh: 'mesh',
  image: 'image',
  video: 'video',
  audio: 'audio',
  text: 'text',
  hotspot: 'hotspot',
};

/** All supported event trigger types shown in the Events panel. */
export const EVENT_TRIGGER_TYPES = [
  'Single Click',
  'Double Click',
  'Gesture Pinch',
  'Gesture Grab',
  'Gesture Poke',
];

/** Normalize trigger labels from API / legacy values. */
export function normalizeEventTrigger(trigger) {
  if (!trigger) return 'Single Click';
  const value = String(trigger).trim().toLowerCase().replace(/_/g, ' ');
  if (value === 'double click' || value === 'doubleclick') return 'Double Click';
  if (value === 'single click' || value === 'singleclick') return 'Single Click';
  if (value === 'gesture pinch' || value === 'gesturePinch') return 'Gesture Pinch';
  if (value === 'gesture grab' || value === 'gestureGrab') return 'Gesture Grab';
  if (
    value === 'gesture poke' ||
    value === 'gesturePoke' ||
    value === 'poke' ||
    value === 'poke'
  ) {
    return 'Gesture Poke';
  }
  return trigger;
}

/** Build temp/old id → canonical _id map from API or Redux assets_data. */
export function buildPublishedAssetIdLookup(assetsData = {}) {
  const lookup = new Map();

  const register = (alias, canonical) => {
    if (!alias || !canonical) return;
    lookup.set(String(alias), String(canonical));
  };

  ASSET_LIST_KEYS.forEach((type) => {
    (assetsData[type] || []).forEach((item) => {
      const canonical = item._id || item.id;
      if (!canonical) return;

      [
        canonical,
        item.id,
        item._id,
        item.name,
        item.url,
        item.temp_file_name,
        item.file_name,
        item.asset_file_name,
        item.text_material?.id,
        item.material_settings?.id,
      ].forEach((alias) => register(alias, canonical));
    });
  });

  const particleList =
    assetsData.particle ||
    assetsData.particles ||
    store.getState().particles?.particles ||
    [];
  particleList.forEach((item) => {
    const canonical = item._id || item.id;
    if (!canonical) return;

    [canonical, item.id, item._id, item.name, item.temp_file_name].forEach(
      (alias) => register(alias, canonical)
    );
  });

  return lookup;
}

export function resolvePublishedAssetId(id, scene, assetsData) {
  if (id === null || id === undefined || id === '') return id;
  const str = String(id);

  if (scene) {
    let found = scene.getObjectByName(str);
    if (!found) {
      scene.traverse((obj) => {
        if (found) return;
        if (
          obj._id === str ||
          obj.url === str ||
          obj.temp_file_name === str ||
          obj.file_name === str ||
          obj.userData?.text_material?.id === str ||
          obj.userData?.material_settings?.id === str
        ) {
          found = obj;
        }
      });
    }
    if (found) {
      return String(found._id || found.name || found.url || str);
    }
  }

  const lookup = buildPublishedAssetIdLookup(assetsData);
  return lookup.get(str) || str;
}

export function normalizeEventsForScene(events, lookup) {
  if (!Array.isArray(events) || !events.length) return [];

  return events.map((event) => ({
    ...event,
    trigger: normalizeEventTrigger(event.trigger),
    triggeringObject: lookup.get(String(event.triggeringObject ?? '')) ??
      event.triggeringObject,
    affectedAsset: lookup.get(String(event.affectedAsset ?? '')) ??
      event.affectedAsset,
  }));
}

export function remapEventsForPublish(events, scene, assetsData, triggeringObjectId) {
  if (!Array.isArray(events) || !events.length) return [];

  const canonicalTrigger = resolvePublishedAssetId(
    triggeringObjectId,
    scene,
    assetsData
  );

  return events.map((event) => ({
    ...event,
    trigger: normalizeEventTrigger(event.trigger),
    triggeringObject: resolvePublishedAssetId(
      event.triggeringObject || canonicalTrigger,
      scene,
      assetsData
    ),
    affectedAsset: resolvePublishedAssetId(
      event.affectedAsset,
      scene,
      assetsData
    ),
  }));
}

const EVENT_CAPABLE_DELETE_TYPES = new Set([
  'mesh',
  'image',
  'video',
  'audio',
  'text',
]);

export function isEventCapableSceneObject(sceneObj) {
  return Boolean(
    sceneObj && EVENT_CAPABLE_DELETE_TYPES.has(sceneObj.deleteType)
  );
}

function collectSceneObjectIds(sceneObj) {
  if (!sceneObj) return [];
  return [
    sceneObj._id,
    sceneObj.name,
    sceneObj.url,
    sceneObj.temp_file_name,
    sceneObj.userData?.text_material?.id,
    sceneObj.userData?.material_settings?.id,
  ]
    .filter(Boolean)
    .map(String);
}

function collectAssetIds(asset) {
  if (!asset) return [];
  return [
    asset._id,
    asset.id,
    asset.name,
    asset.url,
    asset.temp_file_name,
    asset.file_name,
    asset.asset_file_name,
    asset.text_material?.id,
    asset.material_settings?.id,
  ]
    .filter(Boolean)
    .map(String);
}

export function findAssetRecordForSceneObject(sceneObj, assetsData = {}) {
  if (!sceneObj) return null;

  const type = ASSET_TYPE_BY_DELETE_TYPE[sceneObj.deleteType];
  if (!type) return null;

  const objectIds = collectSceneObjectIds(sceneObj);
  if (!objectIds.length) return null;

  const list = assetsData[type] || [];
  return (
    list.find((asset) =>
      collectAssetIds(asset).some((id) => objectIds.includes(id))
    ) || null
  );
}

function normalizeEventList(events) {
  if (!Array.isArray(events)) return [];
  return events.map((event) => ({
    ...event,
    trigger: normalizeEventTrigger(event.trigger),
  }));
}

function withoutInternalPivotEvents(events) {
  return normalizeEventList(events).filter(
    (event) => !isInternalPivotRestoreEvent(event)
  );
}

function withPreservedPivotRestoreEvents(sceneObj, nextEvents) {
  const prev = sceneObj?.events || sceneObj?.userData?.events || [];
  const preserved = normalizeEventList(prev).filter(isInternalPivotRestoreEvent);
  const incomingPivot = normalizeEventList(nextEvents).filter(
    isInternalPivotRestoreEvent
  );
  const next = withoutInternalPivotEvents(nextEvents);
  const pivot =
    incomingPivot[incomingPivot.length - 1] ||
    preserved[preserved.length - 1] ||
    null;
  return pivot ? [...next, pivot] : next;
}

/** Clone frozen Redux/Immer layers before writing event data onto scene objects. */
function ensureWritableUserData(sceneObj) {
  if (!sceneObj) return;
  if (!sceneObj.userData || Object.isFrozen(sceneObj.userData)) {
    sceneObj.userData = { ...(sceneObj.userData || {}) };
  }
}

function setWritableObjectProperty(target, key, value) {
  if (!target) return;
  const descriptor = Object.getOwnPropertyDescriptor(target, key);
  if (!descriptor || descriptor.writable !== false) {
    try {
      target[key] = value;
      return;
    } catch (_) {
      // Property may be frozen (e.g. after rename / Redux sync) — redefine below.
    }
  }
  Object.defineProperty(target, key, {
    value,
    writable: true,
    enumerable: true,
    configurable: true,
  });
}

export function attachEventsToSceneObject(sceneObj, events) {
  if (!sceneObj || !Array.isArray(events)) return;
  const normalizedEvents = withPreservedPivotRestoreEvents(
    sceneObj,
    events
  ).map((event) => ({
    ...event,
  }));

  ensureWritableUserData(sceneObj);
  setWritableObjectProperty(sceneObj, 'events', normalizedEvents);
  setWritableObjectProperty(sceneObj.userData, 'events', normalizedEvents);

  if (sceneObj.userData.text_material) {
    sceneObj.userData.text_material = {
      ...sceneObj.userData.text_material,
      events: normalizedEvents,
    };
  }
}

/**
 * Clone events onto a duplicated asset.
 * triggeringObject and affectedAsset default to the new duplicate id.
 */
export function cloneEventsForDuplicatedAsset(
  events,
  sourceRefs = [],
  newAssetId
) {
  if (!newAssetId) {
    return Array.isArray(events)
      ? withoutInternalPivotEvents(events).map((event) => ({ ...event }))
      : [];
  }

  const to = String(newAssetId);

  return withoutInternalPivotEvents(events).map((event) => ({
    ...event,
    triggeringObject: to,
    // Default the target to the duplicate itself (not the original).
    affectedAsset: to,
  }));
}

/**
 * Clone source events onto a duplicated scene object (and its userData).
 * @returns {Array} remapped events
 */
export function attachClonedEventsToDuplicate(
  duplicatedObject,
  { events, sourceItem, sourceObject } = {}
) {
  if (!duplicatedObject) return [];

  const newId =
    duplicatedObject.name ||
    duplicatedObject._id ||
    duplicatedObject.url ||
    duplicatedObject.temp_file_name ||
    duplicatedObject.userData?.text_material?.id;

  const sourceEvents =
    events ||
    sourceItem?.events ||
    sourceItem?.Events ||
    sourceObject?.events ||
    sourceObject?.Events ||
    sourceObject?.userData?.events ||
    sourceObject?.userData?.text_material?.events ||
    [];

  const cloned = cloneEventsForDuplicatedAsset(
    sourceEvents,
    [sourceItem, sourceObject],
    newId
  );
  attachEventsToSceneObject(duplicatedObject, cloned);
  if (cloned.length > 0) {
    toast(
      'Duplicate created. Event target set to itself.',
      { duration: 3500 }
    );
  }
  return cloned;
}

/** Read user-facing events (hides internal pivot-restore payloads). */
export function getEventsForSceneObject(sceneObj, assetsData = {}) {
  if (!sceneObj) return [];

  const objectEvents = sceneObj.events || sceneObj.userData?.events;
  if (Array.isArray(objectEvents) && objectEvents.length > 0) {
    return withoutInternalPivotEvents(objectEvents);
  }

  const asset = findAssetRecordForSceneObject(sceneObj, assetsData);
  const assetEvents = asset?.events || asset?.Events;
  if (Array.isArray(assetEvents) && assetEvents.length > 0) {
    return withoutInternalPivotEvents(assetEvents);
  }

  return withoutInternalPivotEvents(objectEvents);
}

/** Raw events including internal pivot-restore (publish / hydrate). */
export function getAllEventsForSceneObject(sceneObj, assetsData = {}) {
  if (!sceneObj) return [];

  const objectEvents = sceneObj.events || sceneObj.userData?.events;
  if (Array.isArray(objectEvents) && objectEvents.length > 0) {
    return normalizeEventList(objectEvents);
  }

  const asset = findAssetRecordForSceneObject(sceneObj, assetsData);
  const assetEvents = asset?.events || asset?.Events;
  if (Array.isArray(assetEvents) && assetEvents.length > 0) {
    return normalizeEventList(assetEvents);
  }

  return normalizeEventList(objectEvents);
}

/** Attach Redux / API events onto the selected scene object when missing locally. */
export function hydrateSceneObjectEvents(sceneObj, assetsData = {}, scene = null) {
  if (!sceneObj) return [];

  const liveObj =
    (scene &&
      findSceneObjectByEventAssetId(
        scene,
        sceneObj.name || sceneObj._id || sceneObj.url,
        assetsData
      )) ||
    sceneObj;

  const events = getAllEventsForSceneObject(liveObj, assetsData);
  if (events.length > 0) {
    // Replace wholesale from source of truth (includes pivot restore from API).
    ensureWritableUserData(liveObj);
    setWritableObjectProperty(liveObj, 'events', events);
    setWritableObjectProperty(liveObj.userData, 'events', events);
    if (liveObj.userData.text_material) {
      liveObj.userData.text_material = {
        ...liveObj.userData.text_material,
        events,
      };
    }
  }
  return getEventsForSceneObject(liveObj, assetsData);
}

/** After load: attach normalized events from API payload onto live scene meshes. */
export function syncLoadedAssetEvents(
  scene,
  contentData,
  { onlyIfMissing = false } = {}
) {
  if (!scene || !contentData) return;

  const lookup = buildPublishedAssetIdLookup(contentData);

  ASSET_LIST_KEYS.forEach((type) => {
    (contentData[type] || []).forEach((asset) => {
      const rawEvents = asset.events || asset.Events;
      if (!Array.isArray(rawEvents) || !rawEvents.length) return;

      const sceneObj = resolveSceneObject(scene, asset, type);
      if (!sceneObj) return;

      const existingEvents =
        sceneObj.events ||
        sceneObj.userData?.events ||
        sceneObj.userData?.text_material?.events ||
        [];
      if (onlyIfMissing && existingEvents.length > 0) {
        return;
      }

      attachEventsToSceneObject(
        sceneObj,
        normalizeEventsForScene(rawEvents, lookup)
      );
    });
  });

  const particleList =
    contentData.particle ||
    contentData.particles ||
    store.getState().particles?.particles ||
    [];
  particleList.forEach((particle) => {
    const rawEvents = particle.events || particle.Events;
    if (!Array.isArray(rawEvents) || !rawEvents.length) return;

    const particleId = particle.id || particle._id;
    const sceneObj = particleId ? scene.getObjectByName(particleId) : null;
    if (!sceneObj) return;

    const existingEvents =
      sceneObj.events ||
      sceneObj.userData?.events ||
      [];
    if (onlyIfMissing && existingEvents.length > 0) {
      return;
    }

    attachEventsToSceneObject(
      sceneObj,
      normalizeEventsForScene(rawEvents, lookup)
    );
  });
}

export function syncPreviewAssetEvents(scene) {
  if (!scene) return;
  const state = store.getState();
  const assetsData = state.assets_object?.assets_data || {};
  const particles = state.particles?.particles || [];
  syncLoadedAssetEvents(
    scene,
    { ...assetsData, particle: particles },
    { onlyIfMissing: true }
  );
}

/** Keep Redux assets_data.events aligned with the live scene object. */
export function updateAssetEventsInRedux(sceneObject, events) {
  if (!sceneObject) return;

  const normalizedEvents = withPreservedPivotRestoreEvents(
    sceneObject,
    events || []
  );

  if (sceneObject.deleteType === 'particle') {
    const particleId = sceneObject.name || sceneObject._id;
    if (!particleId) return;
    store.dispatch(
      updateParticle({
        id: particleId,
        updates: { events: normalizedEvents },
      })
    );
    return;
  }

  const type = ASSET_TYPE_BY_DELETE_TYPE[sceneObject.deleteType];
  if (!type) return;

  const objectIds = [
    sceneObject._id,
    sceneObject.name,
    sceneObject.url,
    sceneObject.temp_file_name,
  ]
    .filter(Boolean)
    .map(String);

  if (!objectIds.length) return;

  const state = store.getState();
  const list = state.assets_object?.assets_data?.[type] || [];
  if (!list.length) return;

  let changed = false;
  const updated = list.map((asset) => {
    const assetIds = [
      asset._id,
      asset.id,
      asset.name,
      asset.url,
      asset.temp_file_name,
      asset.text_material?.id,
    ]
      .filter(Boolean)
      .map(String);

    if (!assetIds.some((id) => objectIds.includes(id))) {
      return asset;
    }

    changed = true;
    if (type === 'text' && asset.text_material) {
      return {
        ...asset,
        events: normalizedEvents,
        text_material: { ...asset.text_material, events: normalizedEvents },
      };
    }
    return { ...asset, events: normalizedEvents };
  });

  if (changed) {
    store.dispatch(update_assets_data({ type, data: updated }));
  }
}

export function remapSceneEventAssetIds(scene, oldId, newId) {
  if (!scene || !oldId || !newId || String(oldId) === String(newId)) return;

  const from = String(oldId);
  const to = String(newId);

  const remapId = (value) => (String(value) === from ? to : value);

  const remapList = (list) => {
    if (!Array.isArray(list)) return list;
    let changed = false;
    const next = list.map((event) => {
      const triggeringObject = remapId(event.triggeringObject);
      const affectedAsset = remapId(event.affectedAsset);
      if (
        triggeringObject !== event.triggeringObject ||
        affectedAsset !== event.affectedAsset
      ) {
        changed = true;
        return { ...event, triggeringObject, affectedAsset };
      }
      return event;
    });
    return changed ? next : list;
  };

  const assignEvents = (obj, key, list) => {
    const remapped = remapList(list);
    if (remapped === list) return;
    try {
      obj[key] = remapped;
    } catch {
      Object.defineProperty(obj, key, {
        value: remapped,
        writable: true,
        configurable: true,
        enumerable: true,
      });
    }
  };

  scene.traverse((obj) => {
    if (Array.isArray(obj.events)) {
      assignEvents(obj, 'events', obj.events);
    }

    if (!obj.userData) return;

    let userData = obj.userData;
    let userDataChanged = false;

    if (Array.isArray(userData.events)) {
      const remapped = remapList(userData.events);
      if (remapped !== userData.events) {
        userData = { ...userData, events: remapped };
        userDataChanged = true;
      }
    }

    const textMaterial = userData.text_material;
    if (textMaterial && Array.isArray(textMaterial.events)) {
      const remapped = remapList(textMaterial.events);
      if (remapped !== textMaterial.events) {
        userData = {
          ...userData,
          text_material: { ...textMaterial, events: remapped },
        };
        userDataChanged = true;
      }
    }

    if (userDataChanged) {
      obj.userData = userData;
    }
  });
}

export function findSceneObjectByEventAssetId(scene, assetId, assetsData) {
  if (!scene || assetId === null || assetId === undefined) return null;

  const resolved = resolvePublishedAssetId(assetId, scene, assetsData);
  const ids = [resolved, String(assetId)].filter(Boolean);

  for (const id of ids) {
    const direct = scene.getObjectByName(id);
    if (direct) return direct;
  }

  let found = null;
  scene.traverse((obj) => {
    if (found) return;
    if (
      ids.some(
        (id) =>
          obj.name === id ||
          obj._id === id ||
          obj.url === id ||
          obj.file_name === id ||
          obj.temp_file_name === id ||
          obj.userData?.text_material?.id === id ||
          obj.userData?.material_settings?.id === id
      )
    ) {
      found = obj;
    }
  });

  return found;
}

/** Objects the preview raycaster should test (Redux refs + scene fallbacks). */
export function getPreviewInteractableObjects(scene, assetsState = {}) {
  if (!scene) return [];

  const fromRedux = [
    ...(assetsState.sceneMeshes || []),
    ...(assetsState.sceneHotspots || []),
    ...(assetsState.sceneImages || []),
    ...(assetsState.sceneVideos || []),
    ...(assetsState.sceneAudios || []),
    ...(assetsState.sceneText || []),
  ];

  let sceneObjects = fromRedux
    .map((asset) =>
      asset && asset.isObject3D && asset.parent != null
        ? asset
        : scene.getObjectByName(asset?.name || asset?.url || asset?._id)
    )
    .filter(Boolean);

  const seenIds = new Set(sceneObjects.map((o) => o.uuid));

  scene.traverse((obj) => {
    if (seenIds.has(obj.uuid)) return;
    if (
      obj.deleteType === 'video' ||
      obj.deleteType === 'audio' ||
      obj.deleteType === 'text' ||
      obj.deleteType === 'image' ||
      obj.deleteType === 'mesh' ||
      obj.deleteType === 'hotspot' ||
      obj.deleteType === 'particle' ||
      obj.userData?.isHotspot ||
      (obj.material?.map && obj.material.map instanceof THREE.VideoTexture)
    ) {
      seenIds.add(obj.uuid);
      sceneObjects.push(obj);
    }
  });

  return sceneObjects;
}
